
import { useState } from "react";
import { User } from "@supabase/supabase-js";
import { useProfilePictureMutation } from "./useProfilePictureMutation";
import { toast } from "sonner";
import { 
  IMAGE_PATHS,
  FILE_NAMES,
  validateImageFile,
  uploadStorageFile
} from '@/utils/image-storage';
import { logger } from "@/services/LoggingService";
import { supabase } from "@/integrations/supabase/client";

const COMPONENT = 'ProfilePicture';
const MAX_IMAGE_SIZE = 800; // Maximum dimension in pixels

async function optimizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > MAX_IMAGE_SIZE || height > MAX_IMAGE_SIZE) {
        if (width > height) {
          height = Math.round(height * MAX_IMAGE_SIZE / width);
          width = MAX_IMAGE_SIZE;
        } else {
          width = Math.round(width * MAX_IMAGE_SIZE / height);
          height = MAX_IMAGE_SIZE;
        }
      }

      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Use better quality scaling
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to WebP format for better compression
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        
        const optimizedFile = new File([blob], file.name, {
          type: 'image/webp',
          lastModified: Date.now(),
        });
        
        resolve(optimizedFile);
      }, 'image/webp', 0.85); // 85% quality for good balance
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

export function useProfilePicture(user: User, onSuccess?: () => void) {
  const [isUploading, setIsUploading] = useState(false);
  const mutation = useProfilePictureMutation(user.id);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsUploading(true);
      const file = event.target.files?.[0];
      if (!file) return;

      logger.info(COMPONENT, 'Starting file upload', {
        userId: user.id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Validate the file
      validateImageFile(file);
      
      // Optimize image before upload
      const optimizedFile = await optimizeImage(file);
      logger.info(COMPONENT, 'Image optimized', {
        originalSize: file.size,
        optimizedSize: optimizedFile.size
      });

      // Upload to storage using standardized path and upsert
      const uploadedUrl = await uploadStorageFile(
        IMAGE_PATHS.PROFILE(user.id),
        FILE_NAMES.PROFILE_AVATAR,
        optimizedFile,
        {
          upsert: true,
          cacheControl: '3600'
        }
      );

      logger.info(COMPONENT, 'File uploaded successfully', {
        userId: user.id,
        uploadedUrl
      });

      // Update the profile with the new URL
      await mutation.mutateAsync(uploadedUrl);
      onSuccess?.();
    } catch (error: any) {
      logger.error(COMPONENT, 'File upload failed', {
        error: error.message,
        details: error,
        userId: user.id
      });
      toast.error(error.message || "Failed to upload profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const handleAvatarUrlUpdate = async (url: string) => {
    try {
      setIsUploading(true);
      await mutation.mutateAsync(url);
      onSuccess?.();
    } catch (error: any) {
      logger.error(COMPONENT, 'URL update failed', error);
      toast.error(error.message || "Failed to update profile picture URL");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePicture = async () => {
    try {
      setIsUploading(true);
      await mutation.mutateAsync(null);
      onSuccess?.();
    } catch (error: any) {
      logger.error(COMPONENT, 'Remove picture failed', error);
      toast.error(error.message || "Failed to remove profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const handleImportFromIdentity = async () => {
    try {
      setIsUploading(true);
      logger.info(COMPONENT, 'Importing avatar from identity', { userId: user.id });
      const { data, error } = await supabase.functions.invoke('import-profile-avatar', { body: {} });
      if (error) {
        logger.error(COMPONENT, 'Import from identity failed', error);
        throw new Error(error.message || 'Failed to import avatar from identity');
      }
      const url = (data as any)?.url as string | undefined;
      if (!url) {
        throw new Error('No avatar URL returned');
      }
      await mutation.mutateAsync(url);
      onSuccess?.();
      toast.success('Imported profile picture from identity');
    } catch (error: any) {
      logger.error(COMPONENT, 'Import from identity failed', { error });
      toast.error(error.message || 'Failed to import profile picture');
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    handleFileUpload,
    handleAvatarUrlUpdate,
    handleRemovePicture,
    handleImportFromIdentity
  };
}
